import React, { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, Send, CheckCircle2, ExternalLink, X, Tag } from "lucide-react";
import { toast } from "sonner";

import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real Calendly URL for Banana Paraplanners.
const CALENDLY_URL = "https://calendly.com/hello-bananaparaplanners/30min";

const SERVICES = [
  "Basic SOA ($250)",
  "SMSF SOA ($350)",
  "Complex SOA ($450)",
  "Basic ROA ($150)",
  "Complex ROA ($250)",
  "Workflow Automation Consulting",
  "Other / Not Sure Yet",
];

// Map calculator doc labels -> matching SERVICES dropdown entry
const SERVICE_LOOKUP = {
  "Basic SOA": "Basic SOA ($250)",
  "SMSF SOA": "SMSF SOA ($350)",
  "Complex SOA": "Complex SOA ($450)",
  "Basic ROA": "Basic ROA ($150)",
  "Complex ROA": "Complex ROA ($250)",
};

const TIME_SLOTS = [
  "09:00",
  "10:00",
  "11:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
];

const fmtAUD = (n) =>
  `$${Number(n).toLocaleString("en-AU", {
    minimumFractionDigits: Number(n) % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;

const buildQuoteString = (q) =>
  `${q.service} · Extras × ${q.extras} · Projections: ${q.projection} · ${q.priority ? "Priority" : "Standard"} (${q.turnaround}) · Total: ${fmtAUD(q.total)} AUD${q.capped ? " (CAP APPLIED)" : ""}`;

const Booking = () => {
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState({
    name: "",
    fsp_name: "",
    email: "",
    service_needed: "",
    notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Read locked quote from sessionStorage on mount + listen for live lock events
  useEffect(() => {
    const applyQuote = (q) => {
      if (!q) return;
      setQuote(q);
      const matched = SERVICE_LOOKUP[q.service] || "Other / Not Sure Yet";
      setForm((p) => ({
        ...p,
        service_needed: p.service_needed || matched,
      }));
    };

    try {
      const raw = sessionStorage.getItem("bp_quote");
      if (raw) applyQuote(JSON.parse(raw));
    } catch (e) {
      /* ignore */
    }

    const handler = (e) => applyQuote(e.detail);
    window.addEventListener("bp:quote", handler);
    return () => window.removeEventListener("bp:quote", handler);
  }, []);

  const clearQuote = () => {
    sessionStorage.removeItem("bp_quote");
    setQuote(null);
    setForm((p) => ({ ...p, service_needed: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.fsp_name || !form.email || !form.service_needed || !date || !time) {
      toast.error("Please complete every field before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const requested = `${format(date, "EEEE, d MMM yyyy")} @ ${time} AEST`;
      const payload = {
        ...form,
        requested_datetime: requested,
        quoted_fee: quote ? buildQuoteString(quote) : "",
      };
      await axios.post("https://formsubmit.co/ajax/shaan17roxx@gmail.com", payload);
      setSuccess(true);
      toast.success("Booking received. We will confirm shortly.");
      setForm({ name: "", fsp_name: "", email: "", service_needed: "", notes: "" });
      setDate(undefined);
      setTime("");
      clearQuote();
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.message || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Could not submit booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#FACC15] border-b-2 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <span className="inline-block bg-black text-white border-2 border-black px-3 py-1 text-xs font-bold uppercase tracking-widest">
            Book a Discovery Call
          </span>
          <h2 className="font-display font-black mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
            Lock in a 20 minute scoping call.
          </h2>
          <p className="mt-5 text-base md:text-lg text-black/85 leading-relaxed">
            Tell me about your practice, your licensee, and the bottleneck you&apos;d like to remove.
            Pick a time below, or submit the intake form and I&apos;ll come back with a confirmation
            within one business day.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Intake form */}
          <div
            data-testid="booking-form-wrapper"
            className="lg:col-span-7 bg-white border-2 border-black p-6 md:p-10 shadow-brutal-lg"
          >
            {success ? (
              <div data-testid="booking-success" className="text-center py-10">
                <div className="w-16 h-16 mx-auto bg-[#FACC15] border-2 border-black flex items-center justify-center shadow-brutal">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl mt-5">Request received.</h3>
                <p className="mt-3 text-black/75 max-w-md mx-auto">
                  Thanks. Your booking is in. I&apos;ll reply with a Calendly confirmation within one business day. If you want NDA or SLA paperwork in place, we can sign one of each to cover the whole engagement.
                </p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  data-testid="booking-reset"
                  className="mt-6 inline-flex items-center gap-2 bg-black text-white border-2 border-black font-bold uppercase text-sm tracking-wide py-3 px-6 hover:bg-[#FACC15] hover:text-black transition-colors"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5" data-testid="booking-form">
                {quote && (
                  <div
                    data-testid="locked-quote-badge"
                    className="flex items-start justify-between gap-3 bg-[#FACC15] border-2 border-black p-4 shadow-brutal"
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 bg-black text-[#FACC15] shrink-0">
                        <Tag className="w-4 h-4" />
                      </span>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest">Locked Quote</p>
                        <p className="font-display font-black text-2xl leading-none mt-1">
                          {fmtAUD(quote.total)} <span className="text-xs font-bold tracking-widest">AUD</span>
                          {quote.capped && (
                            <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-black text-[#FACC15] px-1.5 py-0.5">Cap</span>
                          )}
                        </p>
                        <p className="text-xs text-black/80 mt-1 leading-snug">
                          {quote.service} · {quote.projection} projections · Extras × {quote.extras} · {quote.priority ? "Priority" : "Standard"} ({quote.turnaround})
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearQuote}
                      data-testid="locked-quote-clear"
                      aria-label="Clear locked quote"
                      className="shrink-0 border-2 border-black bg-white p-1.5 hover:bg-black hover:text-[#FACC15] transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2">Your Name</label>
                    <input
                      type="text"
                      data-testid="booking-input-name"
                      className="input-brutal"
                      placeholder="e.g. Jordan Smith"
                      value={form.name}
                      onChange={update("name")}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2">FSP / Practice Name</label>
                    <input
                      type="text"
                      data-testid="booking-input-fsp"
                      className="input-brutal"
                      placeholder="e.g. Smith Wealth Pty Ltd"
                      value={form.fsp_name}
                      onChange={update("fsp_name")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Work Email</label>
                  <input
                    type="email"
                    data-testid="booking-input-email"
                    className="input-brutal"
                    placeholder="you@yourpractice.com.au"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2">Requested Date</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="booking-date-trigger"
                          className="input-brutal flex items-center justify-between text-left"
                        >
                          <span className={date ? "text-black" : "text-black/50"}>
                            {date ? format(date, "EEE, d MMM yyyy") : "Pick a date"}
                          </span>
                          <CalendarIcon className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="p-0 border-2 border-black rounded-none bg-white shadow-brutal w-auto"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return d < today;
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2">Preferred Time (AEST)</label>
                    <Select value={time} onValueChange={setTime}>
                      <SelectTrigger
                        data-testid="booking-time-trigger"
                        className="input-brutal h-auto py-3 rounded-none border-2 border-black focus:ring-0 focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Pick a time" />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-black rounded-none bg-white shadow-brutal">
                        {TIME_SLOTS.map((t) => (
                          <SelectItem key={t} value={t} className="rounded-none focus:bg-[#FACC15]">
                            {t} AEST
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Service Needed</label>
                  <Select
                    value={form.service_needed}
                    onValueChange={(v) => setForm((p) => ({ ...p, service_needed: v }))}
                  >
                    <SelectTrigger
                      data-testid="booking-service-trigger"
                      className="input-brutal h-auto py-3 rounded-none border-2 border-black focus:ring-0 focus:ring-offset-0"
                    >
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-black rounded-none bg-white shadow-brutal">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="rounded-none focus:bg-[#FACC15]">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest mb-2">Brief Notes (optional)</label>
                  <textarea
                    data-testid="booking-input-notes"
                    rows={3}
                    className="input-brutal resize-none"
                    placeholder="Tell me about your licensee, current bottleneck, or expected SOA volume."
                    value={form.notes}
                    onChange={update("notes")}
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-black text-white border-2 border-black font-bold uppercase text-sm tracking-wide py-4 px-6 hover:bg-[#FACC15] hover:text-black transition-colors disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : (<><Send className="w-4 h-4" /> Submit Booking Request</>)}
                </button>
                <p className="text-xs text-black/60">
                  Submitting this form just starts the conversation. No commitment. NDA and SLA are optional, and only signed if you choose to engage.
                </p>
              </form>
            )}
          </div>

          {/* Calendly embed */}
          <div className="lg:col-span-5">
            <div className="bg-black text-white border-2 border-black p-6 md:p-8 shadow-brutal">
              <h3 className="font-display font-black text-2xl">Prefer a live calendar?</h3>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">
                Use the Calendly widget below to pick a slot directly. It syncs in real time to my
                availability. No email back and forth.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendly-open-link"
                className="mt-5 inline-flex items-center gap-2 bg-[#FACC15] text-black border-2 border-[#FACC15] font-bold uppercase text-xs tracking-wide py-2 px-4 hover:bg-white hover:text-black hover:border-white transition-colors"
              >
                Open in new tab <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div
              data-testid="calendly-embed"
              className="mt-6 bg-white border-2 border-black overflow-hidden shadow-brutal"
              style={{ minHeight: "520px" }}
            >
              {/* Calendly inline widget — integration placeholder.
                  Drop your Calendly URL into CALENDLY_URL above to go live. */}
              <iframe
                title="Calendly, Banana Paraplanner"
                src={CALENDLY_URL}
                style={{ width: "100%", height: "560px", border: "0" }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
